import {
  TrashIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  AcceptSharingInvitationDocument,
  CreateSharingInvitationDocument,
  DeclineSharingInvitationDocument,
  DeleteSharingInvitationDocument,
  OrganizationSharingsDocument,
  SharingInvitationFragment,
  SharingInvitationsDocument,
  SharingInvitationStatus,
  SharingInvitationType,
} from "../../../../__generated__/graphql";
import { cn, formatDate, statusToName } from "../../../../lib/utils";
import useCurrentOrgInvitations from "./hooks/useCurrentOrgInvitations";
import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";
import Avatar from "../../../../components/Avatar";
import { FaCheck, FaXmark } from "react-icons/fa6";
import useNav from "../../../../hooks/useNavigation";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useRef, useEffect } from "react";

interface MenuWithPortalProps {
  menuButton: React.ReactNode;
  children: React.ReactNode;
}

function MenuWithPortal({ menuButton, children }: MenuWithPortalProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && menuContainerRef.current) {
        // Find the MenuItems element - it should be a direct child with the fixed class
        const menuItems = menuContainerRef.current.querySelector(
          ".fixed"
        ) as HTMLElement;
        if (menuItems) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const isOpen =
            menuItems.getAttribute("data-headlessui-state") !== "closed" &&
            menuItems.style.display !== "none" &&
            !menuItems.classList.contains("hidden");

          if (isOpen) {
            menuItems.style.position = "fixed";
            menuItems.style.top = `${buttonRect.bottom + 4}px`;
            menuItems.style.right = `${window.innerWidth - buttonRect.right}px`;
            menuItems.style.left = "auto";
            menuItems.style.bottom = "auto";
          }
        }
      }
    };

    const observer = new MutationObserver(updatePosition);

    if (menuContainerRef.current) {
      observer.observe(menuContainerRef.current, {
        attributes: true,
        attributeFilter: ["data-headlessui-state", "style", "class"],
        subtree: true,
        childList: true,
      });
    }

    // Also update on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    // Use requestAnimationFrame for initial positioning to ensure DOM is ready
    const rafId = requestAnimationFrame(updatePosition);

    // Also check periodically when menu might open
    const intervalId = setInterval(updatePosition, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(intervalId);
      observer.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  return (
    <div ref={menuContainerRef}>
      <Menu as="div" className="relative">
        <div
          ref={(el) => {
            if (el) {
              const button = el.querySelector("button");
              if (button) {
                buttonRef.current = button;
              }
            }
          }}
        >
          {menuButton}
        </div>
        <MenuItems
          transition
          className="fixed z-50 mt-1 w-64 origin-top-right rounded-md bg-card border border-border shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <div className="py-1">{children}</div>
        </MenuItems>
      </Menu>
    </div>
  );
}

export default function InvitationsList() {
  const navigateWithLog = useNav();

  const { currentOrganization } = useUser();
  const { data, loading, error } = useCurrentOrgInvitations();
  const [deleteSharingInvitation] = useMutation(
    DeleteSharingInvitationDocument
  );
  const [acceptSharingInvitation] = useMutation(
    AcceptSharingInvitationDocument
  );
  const [declineSharingInvitation] = useMutation(
    DeclineSharingInvitationDocument
  );
  const [createSharingInvitation] = useMutation(
    CreateSharingInvitationDocument
  );

  const receivedInvitations = data?.sharingInvitations
    ?.filter(
      (invitation) => invitation?.fromOrganizationId !== currentOrganization?.id
    )
    .filter(
      (invitation) => invitation?.status === SharingInvitationStatus.Pending
    );

  const sentInvitations = data?.sharingInvitations?.filter(
    (invitation) => invitation?.fromOrganizationId === currentOrganization?.id
  );

  if (loading) return null;
  if (error) return <div>Error: {error.message}</div>;

  const statusStyle: Record<SharingInvitationStatus, string> = {
    [SharingInvitationStatus.Pending]:
      "text-yellow-500 bg-yellow-500/10 rounded-md px-2 py-1",
    [SharingInvitationStatus.Expired]:
      "text-red-500 bg-red-500/10 rounded-md px-2 py-1",
    [SharingInvitationStatus.Redeemed]:
      "text-green-500 bg-green-500/10 rounded-md px-2 py-1",
    [SharingInvitationStatus.Refused]:
      "text-red-500 bg-red-500/10 rounded-md px-2 py-1",
  };

  const handleDeleteSharingInvitation = async (invitationId: string) => {
    try {
      await deleteSharingInvitation({
        variables: { sharingInvitationId: invitationId },
        refetchQueries: [SharingInvitationsDocument],
      });
      toast.success("Invitation supprimée avec succès");
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la suppression de l'invitation"
      );
    }
  };

  const handleAcceptInvitation = async (
    invitation: SharingInvitationFragment
  ) => {
    if (!invitation) return;

    try {
      const result = confirm("Voulez-vous accepter cette invitation ?");
      if (!result) return;

      await acceptSharingInvitation({
        variables: {
          acceptSharingInvitationInput: {
            invitationCode: invitation.invitationCode,
            toOrganizationId: currentOrganization?.id ?? "",
          },
        },
        refetchQueries: [
          SharingInvitationsDocument,
          OrganizationSharingsDocument,
        ],
      });

      toast.success("Invitation acceptée");
      navigateWithLog("/collaborators?tab=actives");
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de l'acceptation de l'invitation"
      );
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      const result = confirm("Voulez-vous refuser cette invitation ?");
      if (!result) return;

      await declineSharingInvitation({
        variables: { sharingInvitationId: invitationId },
        refetchQueries: [SharingInvitationsDocument],
      });

      toast.success("Invitation refusée avec succès");
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la suppression de l'invitation"
      );
    }
  };

  const handleResendInvitation = async (
    invitation: SharingInvitationFragment
  ) => {
    if (!invitation) {
      return;
    }

    try {
      const result = confirm("Voulez-vous renvoyer cette invitation ?");
      if (!result) return;

      // First delete the existing invitation
      await deleteSharingInvitation({
        variables: { sharingInvitationId: invitation.id },
      });

      // Then create a new one with the same email and type
      await createSharingInvitation({
        variables: {
          createSharingInvitationInput: {
            recipientEmail: invitation.recipientEmail,
            invitationType: invitation.invitationType,
          },
        },
        refetchQueries: [SharingInvitationsDocument],
      });

      toast.success("Invitation renvoyée avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue lors du renvoi de l'invitation");
    }
  };

  const invitationsTable = () => {
    const allInvitations = [
      ...(receivedInvitations || []),
      ...(sentInvitations || []),
    ].sort((a, b) => {
      // Sort pending invitations first
      if (
        a?.status === SharingInvitationStatus.Pending &&
        b?.status !== SharingInvitationStatus.Pending
      ) {
        return -1;
      }
      if (
        a?.status !== SharingInvitationStatus.Pending &&
        b?.status === SharingInvitationStatus.Pending
      ) {
        return 1;
      }
      return 0;
    });

    return (
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full bg-card">
          <thead className="text-secondary">
            <tr className="bg-dark border-b border-border">
              <th className="text-left p-4 text-sm font-semibold">Type</th>
              <th className="text-left p-4 text-sm font-semibold">De</th>
              <th className="text-left p-4 text-sm font-semibold">
                Destinataire
              </th>
              <th className="text-left p-4 text-sm font-semibold">Statut</th>
              <th className="text-left p-4 text-sm font-semibold">
                Date de création
              </th>
              <th className="text-left p-4 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allInvitations.map((invitation, index) => {
              if (!invitation) return null;

              const isReceived =
                invitation.fromOrganizationId !== currentOrganization?.id;
              const invitationWording: Record<SharingInvitationType, string> = {
                [SharingInvitationType.ExternalMember]: "Collaboration",
                [SharingInvitationType.OrganizationMember]: "Organisation",
              };

              return (
                <tr
                  key={invitation.id}
                  className={cn(
                    "transition-colors border-b border-border",
                    index % 2 === 0
                      ? "bg-dark/50 hover:bg-dark/70"
                      : "bg-dark/20 hover:bg-dark/70",
                    index === allInvitations.length - 1
                      ? "border-b-0"
                      : "border-b border-border"
                  )}
                >
                  <td className="p-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                      {invitationWording[invitation.invitationType]}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Avatar
                        userOrganization={invitation.fromPublicUserOrganization}
                        className="rounded-full flex-shrink-0 w-6 h-6"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-primary">
                          {invitation.fromPublicUserOrganization.name}
                        </span>
                        <span className="text-xs text-secondary">
                          {invitation.fromOrganizationName}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-primary">
                      {isReceived
                        ? "Vous"
                        : invitation.recipientPublicUserOrganization?.name ??
                          invitation.recipientEmail}
                    </span>
                  </td>
                  <td className="p-4">
                    <span
                      className={cn(
                        statusStyle[invitation.status],
                        "text-xs font-medium"
                      )}
                    >
                      {statusToName[invitation.status]}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-secondary">
                      {formatDate(invitation.createdAt)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {isReceived &&
                      invitation.status === SharingInvitationStatus.Pending ? (
                        <>
                          <div
                            className="bg-accent rounded-md p-1 cursor-pointer hover:bg-accent/80"
                            onClick={() => handleAcceptInvitation(invitation)}
                            title="Accepter"
                          >
                            <FaCheck className="w-3 h-3 text-card" />
                          </div>
                          <div
                            className="bg-secondary rounded-md p-1 cursor-pointer hover:bg-secondary/80"
                            onClick={() =>
                              handleDeclineInvitation(invitation.id)
                            }
                            title="Refuser"
                          >
                            <FaXmark className="w-3 h-3 text-card" />
                          </div>
                        </>
                      ) : !isReceived ? (
                        <MenuWithPortal
                          menuButton={
                            <MenuButton className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-dark/50 text-secondary hover:text-primary">
                              <EllipsisVerticalIcon className="w-4 h-4" />
                            </MenuButton>
                          }
                        >
                          <div className="py-1">
                            {invitation.status !==
                              SharingInvitationStatus.Redeemed && (
                              <>
                                <MenuItem>
                                  <button
                                    onClick={() =>
                                      handleResendInvitation(invitation)
                                    }
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-primary hover:bg-dark/50 hover:text-secondary whitespace-nowrap"
                                  >
                                    <ArrowPathIcon className="w-4 h-4" />
                                    Renvoyer l'invitation
                                  </button>
                                </MenuItem>
                                <MenuItem>
                                  <button
                                    onClick={() =>
                                      handleDeleteSharingInvitation(
                                        invitation.id
                                      )
                                    }
                                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 whitespace-nowrap"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                    Supprimer
                                  </button>
                                </MenuItem>
                              </>
                            )}
                            {invitation.status ===
                              SharingInvitationStatus.Redeemed && (
                              <div className="px-4 py-2 text-sm text-secondary">
                                Aucune action disponible
                              </div>
                            )}
                          </div>
                        </MenuWithPortal>
                      ) : (
                        <span className="text-xs text-secondary">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (receivedInvitations?.length === 0 && sentInvitations?.length === 0) {
    return (
      <div className="text-secondary text-sm">
        Pas d'invitations pour le moment
      </div>
    );
  }

  return <div className="mt-8 flex flex-col gap-4">{invitationsTable()}</div>;
}
